import {DialogEvent, DialogEventListener, DialogEventType} from "./types";

/**
 * Tiny synchronous event bus dedicated to dialog lifecycle events.
 *
 * Kept dependency-free (no Node `events`, works in the browser) and decoupled
 * from React so that websocket handlers, analytics, sagas, etc. can subscribe
 * without touching the component tree.
 */
export class DialogEventEmitter {
    private readonly all = new Set<DialogEventListener>();
    private readonly byType = new Map<DialogEventType, Set<DialogEventListener>>();

    /** Subscribe to every event. Returns an unsubscribe function. */
    on(listener: DialogEventListener): () => void;
    /** Subscribe to a single event type. Returns an unsubscribe function. */
    on(type: DialogEventType, listener: DialogEventListener): () => void;
    on(
        typeOrListener: DialogEventType | DialogEventListener,
        maybeListener?: DialogEventListener,
    ): () => void {
        if (typeof typeOrListener === "function") {
            this.all.add(typeOrListener);
            return () => this.all.delete(typeOrListener);
        }
        const type = typeOrListener;
        const listener = maybeListener!;
        const set = this.byType.get(type) ?? new Set();
        set.add(listener);
        this.byType.set(type, set);
        return () => set.delete(listener);
    }

    /** Subscribe once; auto-unsubscribes after the first matching event. */
    once(type: DialogEventType, listener: DialogEventListener): () => void {
        const off = this.on(type, (event) => {
            off();
            listener(event);
        });
        return off;
    }

    emit(event: DialogEvent): void {
        // Snapshot before iterating: listeners may unsubscribe synchronously.
        const typed = this.byType.get(event.type);
        if (typed) {
            for (const l of [...typed]) safeCall(l, event);
        }
        for (const l of [...this.all]) safeCall(l, event);
    }

    clear(): void {
        this.all.clear();
        this.byType.clear();
    }
}

function safeCall(listener: DialogEventListener, event: DialogEvent): void {
    try {
        listener(event);
    } catch (err) {
        // A misbehaving subscriber must never break the orchestration loop.
         
        console.error("[dialog-orchestrator] event listener threw", err);
    }
}
